import { useMemo } from "react";
import { MarkdownReact } from "@/components/MarkdownReact";
import { Card } from "@/components/ui/card";
import { formatWeight } from "@/lib/utils";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
  getQuoteDisplayTotals,
} from "@/lib/quoteCurrency";
import {
  WEIGHT_UNIT_ABBREVIATION,
  type PublicQuote,
  type PublicQuoteLine,
} from "@forgepoint/shared";
import { formatDateOnly } from "@forgepoint/shared";
import {
  formatAddressLines,
  getDiscountLines,
  getBreakWeightUnit,
  getTotalWeightsByUnit,
} from "../utils";

interface PublicQuoteTemplateTwoColumnClassicProps {
  quote: PublicQuote;
  isExpired: boolean;
}

const labelColumnClass =
  "text-xs font-semibold uppercase tracking-wide text-gray-600";

const detailRowClasses = "flex justify-between items-baseline gap-2";

const renderProductRow = (
  line: PublicQuoteLine,
  index: number,
  showTotalWeight: boolean,
  quote: PublicQuote,
) => {
  if (line.type === "SECTION") {
    return (
      <div
        key={`section-${index}`}
        className="pt-4 text-xs font-semibold uppercase tracking-wide text-gray-500"
      >
        {line.description}
      </div>
    );
  }

  if (line.type === "NOTE") {
    return (
      <div
        key={`note-${index}`}
        className="pb-2 flex flex-col gap-1 md:flex-row md:items-start md:justify-between"
      >
        <p className="text-sm text-gray-900">{line.description}</p>
      </div>
    );
  }

  if (line.type === "ITEM") {
    const quantity = line.quantityBreaks?.[0]?.quantity;
    const unitLabel = "units";
    const unitPrice =
      line.quantityBreaks && line.quantityBreaks.length > 0
        ? formatQuoteUnitPrice(line.quantityBreaks[0], quote)
        : null;
    const parsedQuantity = quantity ? Number(quantity) : null;
    const shouldShowQuantityLine =
      parsedQuantity !== null &&
      Number.isFinite(parsedQuantity) &&
      parsedQuantity > 1;
    const weightUnit = getBreakWeightUnit(line.quantityBreaks?.[0], line);

    return (
      <div
        key={`item-${index}`}
        className={`pb-2 flex flex-col gap-1 md:flex-row md:items-start md:justify-between`}
      >
        <div>
          <p className="whitespace-pre-line text-sm text-gray-900">
            {line.description || line.item?.name}
          </p>
        </div>
        <div className="text-right md:text-left">
          <p className="text-sm text-gray-900">
            {line.amount
              ? formatQuoteLineTotal(line.quantityBreaks?.[0], quote)
              : "-"}
          </p>
          {shouldShowQuantityLine && (
            <p className="text-xs text-gray-500">
              {quantity ? `${quantity} ${unitLabel}` : "Custom Qty"}
              {unitPrice && ` · ${unitPrice} each`}
            </p>
          )}
          {showTotalWeight &&
            line.quantityBreaks?.[0]?.totalWeight &&
            weightUnit && (
              <p className="text-xs text-gray-500">
                Weight: {formatWeight(line.quantityBreaks[0].totalWeight)}{" "}
                {WEIGHT_UNIT_ABBREVIATION[weightUnit]}
              </p>
            )}
        </div>
      </div>
    );
  }

  return null;
};

export function PublicQuoteTemplateTwoColumnClassic({
  quote,
  isExpired,
}: PublicQuoteTemplateTwoColumnClassicProps) {
  const discountLines = getDiscountLines(quote.lines ?? []);
  const productLines = (quote.lines ?? []).filter(
    (line) => line.type !== "DISCOUNT",
  );
  const displayTotals = getQuoteDisplayTotals(quote);

  const totalWeightsByUnit = useMemo(
    () => getTotalWeightsByUnit(quote.lines ?? []),
    [quote.lines],
  );

  const detailRows = [
    {
      label: "Quote Number",
      value: `#${quote.quoteDisplayNumber}`,
    },
    {
      label: "Revision",
      value: quote.revision,
    },
    {
      label: "Valid Until",
      value: quote.expirationDate
        ? (formatDateOnly(quote.expirationDate) ?? "No expiration")
        : "No expiration",
    },
  ];

  const renderDetailRow = (
    label: string,
    value: string | number | null | undefined,
  ) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    return (
      <div className={detailRowClasses} key={label}>
        <div className={labelColumnClass}>{label}</div>
        <div className="text-sm text-gray-900 text-right">{value}</div>
      </div>
    );
  };

  const renderMarkdownDetail = (label: string, content: string | null) => {
    if (!content) {
      return null;
    }
    return (
      <div key={label}>
        <div className={`${labelColumnClass} mb-1`}>{label}</div>
        <div className="text-sm text-gray-800 leading-relaxed">
          <MarkdownReact>{content}</MarkdownReact>
        </div>
      </div>
    );
  };

  return (
    <>
      {quote.enableCoverLetter && quote.coverLetterMarkdown && (
        <Card
          className="bg-white py-6 px-4 sm:py-10 sm:px-8 md:py-16 md:px-24 mb-6 shadow-sm print:border-none print:shadow-none print:rounded-none"
          style={{ breakAfter: "page", pageBreakAfter: "always" }}
        >
          <div className="prose prose-sm max-w-none text-gray-800">
            <MarkdownReact>{quote.coverLetterMarkdown}</MarkdownReact>
          </div>
        </Card>
      )}
      <Card className="bg-white py-6 px-4 sm:py-10 sm:px-8 md:py-16 md:px-24 shadow-sm print:border-none print:shadow-none print:rounded-none">
        <div className="space-y-6 md:space-y-10">
          <section className="flex flex-col gap-4 md:flex-row md:gap-6 md:items-start">
            <div className="flex-1 space-y-2 text-left">
              <div className="flex items-start gap-3 sm:gap-4">
                {quote.organization?.logoUrl && (
                  <img
                    src={quote.organization.logoUrl}
                    alt={`${quote.organization.name} logo`}
                    className="h-12 w-12 sm:h-16 sm:w-16 object-contain flex-shrink-0"
                  />
                )}
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-500 truncate">
                    {quote.organization?.name || "Statement"}
                  </p>
                  <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 break-words">
                    {quote.name || "Statement of Work"}
                  </h1>
                </div>
              </div>
              {isExpired && quote.expirationDate && (
                <p className="text-xs font-semibold text-red-600">
                  Expired on{" "}
                  {formatDateOnly(quote.expirationDate, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}
            </div>
            <div className="space-y-2 border border-gray-200 rounded-md p-3 sm:p-4 w-full md:w-auto md:min-w-[200px] md:max-w-[280px]">
              {detailRows.map(({ label, value }) =>
                renderDetailRow(label, value),
              )}
            </div>
          </section>

          {quote.shippingAddress && (
            <section className="flex flex-col gap-3 md:flex-row md:gap-6">
              <div className="md:w-1/4 flex-shrink-0">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                  Ship To
                </h3>
              </div>
              <div className="text-sm text-gray-800 leading-relaxed flex-1">
                {quote.shippingAddress.name && (
                  <p className="font-medium">{quote.shippingAddress.name}</p>
                )}
                {formatAddressLines(quote.shippingAddress)?.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </section>
          )}

          <section className="flex flex-col gap-3 md:flex-row md:gap-6">
            <div className="md:w-1/4 flex-shrink-0">
              <h2 className="text-sm font-semibold text-gray-900">
                Product & Pricing:
              </h2>
            </div>
            <div className="space-y-4 flex-1">
              <div>
                {productLines.map((line, index) =>
                  renderProductRow(
                    line,
                    index,
                    quote.showTotalWeight ?? false,
                    quote,
                  ),
                )}
              </div>
              {quote.showTotals &&
                (quote.subtotal ||
                  discountLines.length > 0 ||
                  quote.subtotalWithDiscounts ||
                  quote.total) && (
                  <div className="space-y-2 pt-3 sm:pt-4 border-t border-gray-100 mt-2">
                    {quote.subtotal && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium">
                          {displayTotals.subtotalFormatted}
                        </span>
                      </div>
                    )}
                    {discountLines.length > 0 && quote.subtotal && (
                      <div className="space-y-1">
                        {discountLines.map((line, idx) => {
                          if (!line.discount || line.amount === null) {
                            return null;
                          }
                          const discountAmount = formatQuoteLineTotal(
                            line,
                            quote,
                          );
                          return (
                            <div
                              key={`${line.description}-${idx}`}
                              className="flex justify-between text-sm text-green-700"
                            >
                              <span>{line.description}</span>
                              <span>-{discountAmount}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {quote.subtotalWithDiscounts &&
                      quote.subtotalWithDiscounts !== quote.subtotal && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Subtotal (after discounts)
                          </span>
                          <span className="font-medium">
                            {displayTotals.subtotalWithDiscountsFormatted}
                          </span>
                        </div>
                      )}
                    {quote.total && (
                      <div className="flex justify-between text-sm sm:text-base font-semibold text-gray-900 pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span>{displayTotals.totalFormatted}</span>
                      </div>
                    )}
                    {quote.showTotalWeight &&
                      totalWeightsByUnit.length > 0 &&
                      totalWeightsByUnit.map(([unit, weight]) => (
                        <div
                          key={unit}
                          className="flex justify-between text-sm text-gray-600 pt-1"
                        >
                          <span>
                            Total Weight ({WEIGHT_UNIT_ABBREVIATION[unit]})
                          </span>
                          <span>{weight.toFixed(2)}</span>
                        </div>
                      ))}
                  </div>
                )}
            </div>
          </section>

          {quote.quoteFooter && (
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500 font-bold whitespace-pre-wrap">
                {quote.quoteFooter}
              </p>
            </div>
          )}

          {(quote.deliveryTerms || quote.paymentTerms) && (
            <section className="flex flex-col gap-3 md:flex-row md:gap-6">
              <div className="md:w-1/4 flex-shrink-0">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                  Notes
                </h3>
              </div>
              <div className="space-y-4 text-sm text-gray-800 leading-relaxed flex-1">
                {renderMarkdownDetail("Delivery Terms", quote.deliveryTerms)}
                {renderMarkdownDetail("Payment Terms", quote.paymentTerms)}
              </div>
            </section>
          )}

          {quote.termsOfService && (
            <section className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3 text-sm leading-relaxed text-gray-800">
                <MarkdownReact
                  components={{
                    h2: ({ children }) => (
                      <div className="font-semibold text-gray-900 md:col-span-1">
                        {children}
                      </div>
                    ),
                    p: ({ children }) => (
                      <div className="md:col-start-2 md:col-span-2 text-gray-700">
                        {children}
                      </div>
                    ),
                    ul: ({ children }) => (
                      <ul className="md:col-start-2 md:col-span-2 list-disc pl-5 text-gray-700">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="md:col-start-2 md:col-span-2 list-decimal pl-5 text-gray-700">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                  }}
                >
                  {quote.termsOfService}
                </MarkdownReact>
              </div>
            </section>
          )}

          {quote.isSigned && quote.signature && (
            <section className="flex flex-col gap-3 md:flex-row md:gap-6 border-t border-gray-200 pt-6">
              <div className="md:w-1/4 flex-shrink-0">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                  Acceptance
                </h3>
              </div>
              <div className="flex-1 text-center">
                <div className="font-signature text-3xl text-gray-900">
                  {quote.signature.name}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {new Date(quote.signature.signedAt).toLocaleDateString()}
                </div>
              </div>
            </section>
          )}
        </div>
      </Card>
    </>
  );
}
